// THE FREE CHECK'S HAND-IN — an address (and a unit) arriving on the URL, used to FILL THE BOXES and for nothing else
// (WEB-PAY-2, 2026-09-29; the orchestrator's decision under R151/R153: one free check, main's website door, and the
// paid flow here takes over from it).
//
// ONE FREE CHECK. W5's `v2FreeCheck` and this app's own free-check screen were retired; the free check is the site's
// (`FREE_CHECK_URL`), and its call to action can send the owner here with what they typed:
//
//     https://app.proppeal.com/?address=<the address>&unit=<the unit>
//
// A URL IS SOMETHING ANYBODY CAN WRITE, so what arrives on one is TEXT FOR A BOX, never a fact:
//   - it fills the address and unit boxes on `/` and nothing else — not the session's address, not the chip, not an
//     appeal id, a price, a step or a decision. Every other parameter is ignored.
//   - it is SENT NOWHERE until the owner presses the button, and then exactly as the boxes read at that moment, through
//     the same `v2ResolveProperty.address` call typing it would make. The server resolves it and decides everything.
//   - a value longer than the free check itself accepts, or carrying a control character, fills nothing: it is not
//     something the free check could have handed over.
//   - it is read ONCE, and the query is then taken off the address bar, so the address does not sit in the tab's
//     history or in a link someone copies.
//
// `web/test/entry.test.tsx` pins each clause, and each was mutation-probed.

/** The one free check (R151), which this app's `/free-check` hands off to. */
export const FREE_CHECK_URL = "https://proppeal.com/free-check/";

/** The unit box's label: the app's own words (`AppealAce/Features/Checker/PropertyCheckerView.swift` `unitLabel`, the
 *  walkthrough's), not a new sentence. `web/test/entry.test.tsx` reads the Swift source and fails on any difference. */
export const UNIT_LABEL = "Unit / Apt # — condos and apartments only";

/** The query parameter names the free check hands in under. */
export const HAND_IN_PARAMS = { address: "address", unit: "unit" } as const;

/** The longest address a box is filled with: the contract's own `typedAddress` limit (`maxChars: 200`,
 *  functions/src/v2/contract/schema.ts). Pinned to the generated contract by `web/test/entry.test.tsx`. */
export const HAND_IN_ADDRESS_MAX = 200;

/** The longest unit: the free check's own `UNIT_MAX_LENGTH` (functions/src/freecheck/endpoint.ts), past which it
 *  refuses the request, so it cannot have handed one over. Pinned to that constant by `web/test/entry.test.tsx`. */
export const HAND_IN_UNIT_MAX = 40;

export interface HandIn {
  readonly address: string | null;
  readonly unit: string | null;
}

// eslint-disable-next-line no-control-regex -- the point of the pattern is to find control characters.
const CONTROL = /[\u0000-\u001f\u007f]/;

function boxValue(raw: string | null, max: number): string | null {
  if (raw === null) return null;
  const value = raw.trim();
  if (value === "" || value.length > max || CONTROL.test(value)) return null;
  return value;
}

/** What the URL hands in, as text for two boxes. `null` means "fill nothing". */
export function readHandIn(search: string): HandIn {
  const params = new URLSearchParams(search);
  return {
    address: boxValue(params.get(HAND_IN_PARAMS.address), HAND_IN_ADDRESS_MAX),
    unit: boxValue(params.get(HAND_IN_PARAMS.unit), HAND_IN_UNIT_MAX),
  };
}

/** The owner's unit as the contract carries it: what they typed, or null for none. Every resolve call sends the same
 *  address AND unit the entry sent, so a gate re-asking never resolves a different property. */
export function unitOrNull(typed: string): string | null {
  const unit = typed.trim();
  return unit === "" ? null : unit;
}
